import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { Badge, BadgeColors } from '@contrast-security-inc/design-system-badges'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { ThemeFonts } from '@contrast-security-inc/design-system-foundations'
import { CoreMustacheRenderers } from './shared'
import { css } from '@emotion/css'

const contrastUrl = '/Contrast/static/ng/index.html'

// strip the contrast part of the url string if the url starts with it
// jsdom doesn't dispatch the hashchange event in we have a relative url rather than just a hash
export const getContrastHashOrUrl = (url: string) => {
  return url.startsWith(contrastUrl) ? url.replace(contrastUrl, '') : url
}

const badgeFactory = (badgeColor: BadgeColors) => () => (text, render) => {
  const [url, content] = render(text).split('$$LINK_DELIM$$')
  return ReactDOMServer.renderToStaticMarkup(
    <div
      className={css`
        padding-top: 8px;
      `}
    >
      <Link href={getContrastHashOrUrl(url)} font="metadata">
        <Badge color={badgeColor}>{content}</Badge>
      </Link>
    </div>
  )
}

const textRenderFactory =
  (textType: ThemeFonts, as?: keyof React.ReactHTML) =>
  () =>
  (text, render) => {
    return ReactDOMServer.renderToStaticMarkup(
      <Text font={textType} as={as || 'span'} color="neutral100">
        {render(text)}
      </Text>
    )
  }

export const coreSkeletorMustacheRenderers: CoreMustacheRenderers = {
  nl: '<br/>',
  focus: textRenderFactory('bodyStrong'),
  quote: textRenderFactory('metadata', 'div'),
  block: textRenderFactory('body', 'div'),
  greenBadge: badgeFactory('green50'),
  yellowBadge: badgeFactory('yellow50'),
  redBadge: badgeFactory('red50'),
  defaultBadge: badgeFactory('neutral40'),
  link: () => (text, render) => {
    const [url, content] = render(text).split('$$LINK_DELIM$$')
    return ReactDOMServer.renderToStaticMarkup(
      <Link font="body" href={getContrastHashOrUrl(url)}>
        {content}
      </Link>
    )
  },
  clickMethod: () => (text, render) => {
    const [method, content] = render(text).split('$$LINK_DELIM$$')
    const [methodTarget, params] = method.split(':')
    const paramsAttribute = params ? { 'data-click-method-params': params } : {}

    return ReactDOMServer.renderToStaticMarkup(
      <Link font="body" data-click-method={methodTarget} {...paramsAttribute}>
        {content}
      </Link>
    )
  }
}
